import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";

const timezones = [
  { value: "Asia/Kolkata", label: "India Standard Time (IST) - UTC+5:30" },
  { value: "UTC", label: "Coordinated Universal Time (UTC)" },
  { value: "America/New_York", label: "Eastern Standard Time (EST) - UTC-5:00" },
  { value: "Europe/London", label: "Greenwich Mean Time (GMT) - UTC+0:00" },
  { value: "Asia/Singapore", label: "Singapore Time (SGT) - UTC+8:00" }
];

const dateFormats = [
  { value: "DD-MM-YYYY", label: "DD-MM-YYYY (e.g. 26-07-2026)" },
  { value: "MM-DD-YYYY", label: "MM-DD-YYYY (e.g. 07-26-2026)" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD (e.g. 2026-07-26)" },
  { value: "DD MMM YYYY", label: "DD MMM YYYY (e.g. 26 Jul 2026)" }
];

export default function SystemSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editingTeamName, setEditingTeamName] = useState("");
  const [settings, setSettings] = useState({
    theme: "light",
    dateFormat: "DD-MM-YYYY",
    timeFormat: "12-hour",
    timezone: "Asia/Kolkata",
    currency: "INR",
    measurementUnits: "mm",
    pdfDefaults: {
      pageSize: "A4",
      margin: "normal",
      showLetterhead: true
    },
    notificationSettings: {
      emailAlerts: true,
      whatsAppAlerts: false
    },
    teams: []
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BASE_URL + "/api/admin-settings/system-settings", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setSettings(res.data || {});
    } catch (err) {
      console.error(err);
      alert("Failed to load system settings preferences.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      setSaving(true);
      const res = await axios.put(API_BASE_URL + "/api/admin-settings/system-settings", settings, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setSettings(res.data);
      alert("System Settings updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to update system settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddTeam = () => {
    if (!newTeamName.trim()) return;
    const name = newTeamName.trim();
    const teams = Array.isArray(settings.teams) ? settings.teams : [];
    if (teams.some(t => t.name.toLowerCase() === name.toLowerCase())) {
      alert("A team with this name already exists.");
      return;
    }
    const id = "team_" + Date.now();
    setSettings(prev => ({
      ...prev,
      teams: [...(Array.isArray(prev.teams) ? prev.teams : []), { id, name }]
    }));
    setNewTeamName("");
  };

  const handleStartEditTeam = (team) => {
    setEditingTeamId(team.id);
    setEditingTeamName(team.name);
  };

  const handleSaveEditTeam = () => {
    if (!editingTeamName.trim()) return;
    const name = editingTeamName.trim();
    setSettings(prev => ({
      ...prev,
      teams: (Array.isArray(prev.teams) ? prev.teams : []).map(t =>
        t.id === editingTeamId ? { ...t, name } : t
      )
    }));
    setEditingTeamId(null);
    setEditingTeamName("");
  };

  const handleRemoveTeam = (teamId) => {
    if (!window.confirm("Are you sure you want to remove this team?")) return;
    setSettings(prev => ({
      ...prev,
      teams: (Array.isArray(prev.teams) ? prev.teams : []).filter(t => t.id !== teamId)
    }));
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-500 font-semibold">Loading system settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b pb-4">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">System Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Configure global application variables, default formatting rules, and document preferences.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 font-semibold text-sm shadow-md transition disabled:bg-slate-400"
        >
          <span className="material-symbols-outlined text-sm">save</span>
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>

      <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Localization and Display */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
          <h2 className="text-base font-bold text-slate-850 dark:text-white flex items-center gap-2 border-b pb-2">
            <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">language</span>
            Localization &amp; Interface
          </h2>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Default UI Theme</label>
            <select
              value={settings.theme}
              onChange={(e) => setSettings(prev => ({ ...prev, theme: e.target.value }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition"
            >
              <option value="light">Always Light Mode</option>
              <option value="dark">Always Dark Mode</option>
              <option value="system">Follow System Default</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Date Format</label>
            <select
              value={settings.dateFormat}
              onChange={(e) => setSettings(prev => ({ ...prev, dateFormat: e.target.value }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition"
            >
              {dateFormats.map(df => (
                <option key={df.value} value={df.value}>{df.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Time Format</label>
            <select
              value={settings.timeFormat}
              onChange={(e) => setSettings(prev => ({ ...prev, timeFormat: e.target.value }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition"
            >
              <option value="12-hour">12-hour (e.g. 06:45 PM)</option>
              <option value="24-hour">24-hour (e.g. 18:45)</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Default System Timezone</label>
            <select
              value={settings.timezone}
              onChange={(e) => setSettings(prev => ({ ...prev, timezone: e.target.value }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition"
            >
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Currency & Business formats */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
          <h2 className="text-base font-bold text-slate-850 dark:text-white flex items-center gap-2 border-b pb-2">
            <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400">payments</span>
            Currency &amp; Measurements
          </h2>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Base Currency Symbol</label>
            <select
              value={settings.currency}
              onChange={(e) => setSettings(prev => ({ ...prev, currency: e.target.value }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition"
            >
              <option value="INR">INR (₹) - Indian Rupee</option>
              <option value="USD">USD ($) - United States Dollar</option>
              <option value="EUR">EUR (€) - Euro</option>
              <option value="GBP">GBP (£) - British Pound</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Measurement Units</label>
            <select
              value={settings.measurementUnits}
              onChange={(e) => setSettings(prev => ({ ...prev, measurementUnits: e.target.value }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition"
            >
              <option value="mm">mm - Millimeters</option>
              <option value="cm">cm - Centimeters</option>
              <option value="inch">inch - Inches</option>
              <option value="mtr">mtr - Meters</option>
            </select>
          </div>
        </div>

        {/* Manage Teams Section */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
          <h2 className="text-base font-bold text-slate-850 dark:text-white flex items-center gap-2 border-b pb-2">
            <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">group</span>
            Manage Teams
          </h2>

          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Enter new team name"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              className="flex-1 border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2 text-sm outline-none transition"
            />
            <button
              type="button"
              onClick={handleAddTeam}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-4 py-2 font-semibold text-sm transition"
            >
              Add Team
            </button>
          </div>

          <div className="space-y-2 mt-4 max-h-60 overflow-y-auto">
            {(Array.isArray(settings.teams) ? settings.teams : []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">No teams configured yet.</p>
            ) : (
              (settings.teams || []).map((team) => (
                <div key={team.id} className="flex items-center justify-between border border-slate-100 dark:border-slate-700 p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50">
                  {editingTeamId === team.id ? (
                    <div className="flex-1 flex gap-2">
                      <input
                        type="text"
                        value={editingTeamName}
                        onChange={(e) => setEditingTeamName(e.target.value)}
                        className="flex-1 border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-3 py-1 text-sm outline-none transition"
                      />
                      <button
                        type="button"
                        onClick={handleSaveEditTeam}
                        className="bg-emerald-605 hover:bg-emerald-700 text-white rounded-xl px-3 py-1 font-semibold text-xs transition"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingTeamId(null)}
                        className="bg-slate-500 hover:bg-slate-600 text-white rounded-xl px-3 py-1 font-semibold text-xs transition"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm font-semibold text-slate-750 dark:text-slate-200">{team.name}</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleStartEditTeam(team)}
                          className="text-blue-600 hover:text-blue-700 font-semibold text-xs transition"
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveTeam(team.id)}
                          className="text-rose-600 hover:text-rose-700 font-semibold text-xs transition"
                        >
                          Remove
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* PDF Document Layout Settings */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
          <h2 className="text-base font-bold text-slate-850 dark:text-white flex items-center gap-2 border-b pb-2">
            <span className="material-symbols-outlined text-purple-650 dark:text-purple-400">picture_as_pdf</span>
            PDF Document Defaults
          </h2>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Default Page Size</label>
            <select
              value={settings.pdfDefaults?.pageSize}
              onChange={(e) => setSettings(prev => ({ ...prev, pdfDefaults: { ...prev.pdfDefaults, pageSize: e.target.value } }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition"
            >
              <option value="A4">A4 (Standard Business)</option>
              <option value="Letter">US Letter</option>
              <option value="Legal">Legal</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Print Margin Style</label>
            <select
              value={settings.pdfDefaults?.margin}
              onChange={(e) => setSettings(prev => ({ ...prev, pdfDefaults: { ...prev.pdfDefaults, margin: e.target.value } }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition"
            >
              <option value="normal">Normal (0.75 in)</option>
              <option value="narrow">Narrow (0.25 in)</option>
              <option value="none">No Margin (Full Bleed)</option>
            </select>
          </div>

          <label className="flex items-center gap-3 cursor-pointer mt-2 bg-slate-50 dark:bg-slate-700/20 border p-3 rounded-xl">
            <input
              type="checkbox"
              checked={!!settings.pdfDefaults?.showLetterhead}
              onChange={(e) => setSettings(prev => ({ ...prev, pdfDefaults: { ...prev.pdfDefaults, showLetterhead: e.target.checked } }))}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Show Branding Letterhead on exported files</span>
          </label>
        </div>

        {/* Notifications and Alerts */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
          <h2 className="text-base font-bold text-slate-850 dark:text-white flex items-center gap-2 border-b pb-2">
            <span className="material-symbols-outlined text-orange-600 dark:text-orange-400">notifications_active</span>
            Notification Preferences
          </h2>

          <div className="flex flex-col gap-3 bg-slate-50 dark:bg-slate-700/20 border p-4 rounded-xl">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!settings.notificationSettings?.emailAlerts}
                onChange={(e) => setSettings(prev => ({ ...prev, notificationSettings: { ...prev.notificationSettings, emailAlerts: e.target.checked } }))}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Send system email updates to manager</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!settings.notificationSettings?.whatsAppAlerts}
                onChange={(e) => setSettings(prev => ({ ...prev, notificationSettings: { ...prev.notificationSettings, whatsAppAlerts: e.target.checked } }))}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Trigger WhatsApp confirmation alerts</span>
            </label>
          </div>
        </div>

      </form>
    </div>
  );
}
